import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import './Welcome.css';
import '@fontsource/bebas-neue';
import { useTranslation } from 'react-i18next';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { useMusic } from './hooks/useMusic';
import { playSoundEffect } from './hooks/useSoundEffects';
import { API_URL } from './config/apiConfig';
import StrategyPreferenceRewardModal from './StrategyPreferenceRewardModal';
import { TutorialMessageBox } from './tutorial/index';
import { useContainerScale } from './hooks/useViewportScale';
import AchievementUnlockedToast from './notifications/AchievementUnlockedToast';
import { usePendingAchievementToasts } from './hooks/usePendingAchievementToasts';

const STRATEGY_CHOICES = [
  { key: 'aggressive', value: 'Aggressive' },
  { key: 'greedy', value: 'Greedy' },
  { key: 'patient', value: 'Patient' },
  { key: 'controlling', value: 'Controlling' },
  { key: 'creative', value: 'Creative' }
];

function Welcome({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [appVersion, setAppVersion] = useState('0.1.8');
  const [testersOnlineCount, setTestersOnlineCount] = useState(null);
  const [testersOnlineFetchFailed, setTestersOnlineFetchFailed] = useState(false);
  
  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();
  
  // Strategy preference and tutorial state
  const [hasSelectedStrategyPreference, setHasSelectedStrategyPreference] = useState(null); // null = not checked yet
  const [seenTutorialMessages, setSeenTutorialMessages] = useState([]);
  const [isAlwaysShowUser, setIsAlwaysShowUser] = useState(false);
  const [showTutorial1, setShowTutorial1] = useState(false);
  const [showTutorial2, setShowTutorial2] = useState(false);
  const [showStrategyRewardModal, setShowStrategyRewardModal] = useState(false);
  const [grantedOrders, setGrantedOrders] = useState([]);
  const [selectingPreference, setSelectingPreference] = useState(false);
  
  // Function to fetch version from web (version.json)
  const fetchWebVersion = async () => {
    try {
      const response = await fetch('/version.json');
      if (response.ok) {
        const data = await response.json();
        if (data.version) {
          setAppVersion(data.version);
        }
      } else {
        console.warn('Could not fetch version.json, using default');
      }
    } catch (err) {
      console.warn('Could not fetch app version:', err);
    }
  };
  
  // Get app version from Electron or web
  useEffect(() => {
    // Try Electron first
    if (window.electron && window.electron.getAppVersion) {
      window.electron.getAppVersion().then(version => {
        setAppVersion(version);
      }).catch(err => {
        console.warn('Could not get app version from Electron:', err);
        // Fallback to web version fetch
        fetchWebVersion();
      });
    } else {
      // Web version: fetch from version.json
      fetchWebVersion();
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const POLL_MS = 12000;

    const loadCount = async () => {
      try {
        const response = await fetch(`${API_URL}/testers-online`);
        if (!response.ok) {
          const errText = await response.text().catch(() => response.statusText);
          throw new Error(`testers-online HTTP ${response.status}: ${errText}`);
        }
        const data = await response.json();
        if (typeof data.count !== 'number' || !Number.isFinite(data.count)) {
          throw new Error('testers-online response missing numeric count');
        }
        if (!cancelled) {
          setTestersOnlineCount(Math.max(0, Math.floor(data.count)));
          setTestersOnlineFetchFailed(false);
        }
      } catch (err) {
        console.error('Welcome: testers-online fetch failed:', err);
        if (!cancelled) {
          setTestersOnlineFetchFailed(true);
        }
      }
    };

    loadCount();
    const id = setInterval(loadCount, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Fetch strategy preference status and tutorial status
  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const [prefRes, tutorialRes] = await Promise.all([
        fetch(`${API_URL}/profile/strategy-preference-status`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/tutorial/status`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (prefRes.ok) {
        const data = await prefRes.json();
        setHasSelectedStrategyPreference(data.hasSelected || false);
      }
      if (tutorialRes.ok) {
        const data = await tutorialRes.json();
        setSeenTutorialMessages(data.seenMessages || []);
        setIsAlwaysShowUser(data.isAlwaysShowUser || false);
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  useEffect(() => {
    if (user) {
      fetchStatus();
    }
  }, [user]);

  // ✅ DEV: Check if dev starting rewards are passed via state (for dev demo)
  useEffect(() => {
    if (location.state?.devStartingRewards && Array.isArray(location.state.devStartingRewards)) {
      setGrantedOrders(location.state.devStartingRewards);
      setShowStrategyRewardModal(true);
      // Clear the location state to prevent re-adding on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Show Tutorial 1: normal users when they haven't selected; isAlwaysShowUser (e.g. player1) always
  useEffect(() => {
    const shouldShow = isAlwaysShowUser || (hasSelectedStrategyPreference === false && !seenTutorialMessages.includes('welcome'));
    if (user && shouldShow && !location.state?.devStartingRewards) {
      const timer = setTimeout(() => setShowTutorial1(true), 500);
      return () => clearTimeout(timer);
    }
  }, [user, hasSelectedStrategyPreference, seenTutorialMessages, isAlwaysShowUser, location.state]);

  const handleSelectStrategyPreference = async (preference) => {
    if (selectingPreference) return;
    
    setSelectingPreference(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('You must be logged in to select a strategy preference. Please log in again.');
        return;
      }

      if (isAlwaysShowUser && hasSelectedStrategyPreference) {
        setShowTutorial1(false);
        setShowTutorial2(true);
        playSoundEffect('/sounds/click.mp3');
        setSelectingPreference(false);
        return;
      }

      const res = await fetch(`${API_URL}/profile/select-strategy-preference`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ preference }),
      });
      
      if (res.ok) {
        const data = await res.json();
        setHasSelectedStrategyPreference(true);
        setShowTutorial1(false);
        setGrantedOrders(data.grantedOrders || []);
        setShowStrategyRewardModal(true);
        playSoundEffect('/sounds/click.mp3');

        await fetch(`${API_URL}/tutorial/mark-seen`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ messageKey: 'welcome' }),
        });
        setSeenTutorialMessages(prev => prev.includes('welcome') ? prev : [...prev, 'welcome']);
      } else {
        const errorData = await res.json().catch(() => ({ error: `HTTP ${res.status}: ${res.statusText}` }));
        console.error('Strategy preference selection failed:', errorData);
        alert(errorData.error || `Failed to select strategy preference (${res.status})`);
      }
    } catch (err) {
      console.error('Error selecting strategy preference:', err);
      alert(`Failed to select strategy preference: ${err.message || 'Network error'}`);
    } finally {
      setSelectingPreference(false);
    }
  };

  const handleRewardModalClose = () => {
    playSoundEffect('/sounds/click.mp3');
    setShowStrategyRewardModal(false);
    if (!seenTutorialMessages.includes('command_hub')) {
      setShowTutorial2(true);
    }
  };

  const handleTutorial2Continue = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      await fetch(`${API_URL}/tutorial/mark-seen`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ messageKey: 'command_hub' }),
      });
      setSeenTutorialMessages(prev => prev.includes('command_hub') ? prev : [...prev, 'command_hub']);
    }
    setShowTutorial2(false);
  };
  
  // Play welcome music
  useMusic('/music/somber_2.mp3');
  const { current: pendingToast, dismissCurrent: dismissPendingToast } = usePendingAchievementToasts();

  // Shared button style with welcome_buttons.png background
  // Smaller on mobile (932x430 base)
  const buttonStyle = {
    backgroundImage: 'url(/images/welcome_buttons.png)',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundColor: 'transparent',
    position: 'relative',
    zIndex: 1,
    width: isContainerMobile ? '130px' : '300px',
    height: isContainerMobile ? '42px' : '100px',
    minWidth: isContainerMobile ? '130px' : '300px',
    minHeight: isContainerMobile ? '42px' : '100px',
    padding: isContainerMobile ? '2px 4px' : undefined
  };

  const buttonTextStyle = {
    position: 'relative',
    zIndex: 2,
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    textAlign: 'center',
    lineHeight: isContainerMobile ? '1.0' : '1.2',
    fontSize: isContainerMobile ? '9px' : 'inherit',
    fontWeight: isContainerMobile ? '500' : undefined,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: isContainerMobile ? '120px' : undefined,
    display: isContainerMobile ? '-webkit-box' : undefined,
    WebkitLineClamp: isContainerMobile ? 2 : undefined,
    WebkitBoxOrient: isContainerMobile ? 'vertical' : undefined
  };

  return (
    <div className="welcome-viewport-wrapper">
      {pendingToast && (
        <AchievementUnlockedToast
          titleName={pendingToast.titleName}
          color={pendingToast.color}
          achievementKey={pendingToast.achievementKey}
          onClose={dismissPendingToast}
        />
      )}
      {/* Full-screen background video - outside viewport-container to cover entire screen */}
      <video autoPlay loop muted playsInline className="welcome-fullscreen-video">
        <source src="/background.mp4" type="video/mp4" />
        {t('welcome.browserNoVideo')}
      </video>
      
      {/* Viewport container - scales to fit screen */}
      <div 
        className="welcome-viewport-container"
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        {(user?.username || user) && (
          <div style={{ 
            position: 'absolute', 
            top: isContainerMobile ? 5 : 10, 
            left: isContainerMobile ? 5 : 10, 
            zIndex: 1000, 
            display: 'flex', 
            alignItems: 'center',
            transform: isContainerMobile ? 'scale(0.8)' : 'none',
            transformOrigin: 'top left'
          }}>
            <FriendsIcon />
            <SettingsMenu onLogout={() => {
              playSoundEffect('/sounds/click.mp3');
              onLogout();
              navigate('/');
            }} />
            <span style={{ color: 'white', marginRight: '10px', fontSize: isContainerMobile ? '12px' : 'inherit' }}>{user?.username || user}</span>
          </div>
        )}

      {/* Logo - positioned in viewport space */}
      <img 
        src="/images/logo.png" 
        alt="Chronicles of the Dying: Anthology" 
        style={{
          position: 'absolute',
          top: isContainerMobile ? '10px' : '40px',
          left: isContainerMobile ? '700px' : '1460px', /* Mobile: right side, Desktop: 1460px */
          transform: 'translateX(-50%)',
          height: 'auto',
          width: isContainerMobile ? '200px' : 'auto',
          zIndex: 1,
          objectFit: 'contain'
        }}
      />

      <div 
        className="button-overlay"
        style={isContainerMobile ? {
          left: '466px', /* Center of 932px */
          bottom: '10px',
          gap: '0px',
          flexWrap: 'nowrap',
          justifyContent: 'center',
          width: '900px'
        } : {}}
      >
        <button 
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            navigate('/strategy');
          }}
          style={{...buttonStyle, margin: isContainerMobile ? '0 -15px' : '0 -50px'}}
        >
          <span style={{...buttonTextStyle, whiteSpace: 'pre-line'}}>
            {t('welcome.prepareStrategy')}
          </span>
        </button>
        <button 
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            navigate('/command-center');
          }}
          style={{...buttonStyle, margin: isContainerMobile ? '0 -15px' : '0 -50px'}}
        >
          <span style={{...buttonTextStyle, whiteSpace: 'pre-line'}}>
            {t('welcome.findStrategies')}
          </span>
        </button>
        <button 
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            navigate('/honor-hall');
          }}
          style={{...buttonStyle, margin: isContainerMobile ? '0 -15px' : '0 -50px'}}
        >
          <span style={{...buttonTextStyle, whiteSpace: 'pre-line', fontSize: isContainerMobile ? '8px' : 'inherit'}}>
            {t('welcome.honorHall')}
          </span>
        </button>
        <button 
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            navigate('/battleoverview');
          }}
          style={{...buttonStyle, margin: isContainerMobile ? '0 -15px' : '0 -50px'}}
        >
          <span style={buttonTextStyle}>{t('welcome.battle')}</span>
        </button>
        <button 
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            navigate('/lore');
          }}
          style={{...buttonStyle, margin: isContainerMobile ? '0 -15px' : '0 -50px'}}
        >
          <span style={buttonTextStyle}>{t('welcome.lore')}</span>
        </button>
        <button 
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            navigate('/profile');
          }}
          style={{...buttonStyle, margin: isContainerMobile ? '0 -15px' : '0 -50px'}}
        >
          <span style={buttonTextStyle}>{t('welcome.profile')}</span>
        </button>
        <button 
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            navigate('/bank');
          }}
          style={{...buttonStyle, margin: isContainerMobile ? '0 -15px' : '0 -50px'}}
        >
          <span style={buttonTextStyle}>{t('welcome.bank')}</span>
        </button>
      </div>
      
      {/* Testers online + version (lower right) */}
      <div style={{
        position: 'absolute',
        bottom: isContainerMobile ? 10 : 20,
        right: isContainerMobile ? 10 : 20,
        zIndex: 1000,
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: isContainerMobile ? '0.7em' : '0.9em',
        fontFamily: 'Exo 2, sans-serif',
        textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)',
        textAlign: 'right',
        lineHeight: 1.35
      }}>
        <div>
          Testers online:{' '}
          {testersOnlineFetchFailed && testersOnlineCount === null
            ? '—'
            : testersOnlineCount === null
              ? '…'
              : testersOnlineCount}
        </div>
        <div>v{appVersion}</div>
      </div>
      </div>{/* Close welcome-viewport-container */}

      {/* Tutorial rendered via portal to document.body so it centers on true viewport */}
      {showTutorial1 && createPortal(
        <TutorialMessageBox
          image="/images/tutorial_welcome.png"
          text={t('welcome.tutorial.welcome')}
          choices={STRATEGY_CHOICES}
          onSelect={handleSelectStrategyPreference}
          onCancel={() => setShowTutorial1(false)}
          showCancel={true}
          disabled={selectingPreference}
          voiceKey="welcome.tutorial.welcome"
        />,
        document.body
      )}

      {showTutorial2 && createPortal(
        <TutorialMessageBox
          image="/images/tutorial_wait.png"
          text={t('welcome.tutorial.commandHub')}
          onContinue={handleTutorial2Continue}
          voiceKey="welcome.tutorial.commandHub"
        />,
        document.body
      )}

      <StrategyPreferenceRewardModal
        isOpen={showStrategyRewardModal}
        onClose={handleRewardModalClose}
        grantedOrders={grantedOrders}
      />
    </div>
  );
}

export default Welcome;